import React, { Fragment, useEffect, useRef, useState } from "react";
import { TableInfo } from "api";
import { toPairs } from "ramda";
import { Cell, CellType, CellOptions } from "./Cell";
import { ColumnAutoWidths, MiddleTable } from "./MiddleTable";
import { RightTable } from "./RightTable";
import { LeftTable } from "./LeftTable";
import { ColumnDetail } from "@avamae/table";
import { TCustomCell } from "components/Category/Category";
import capitaliseStringFirstLetter from "helpers/capitaliseStringFirstLetter";
import { CrudOptions, MenuState } from "components/Category/CrudMenu";
import { lowerCaseFirstLetter } from "helpers/lowerCaseFirstLetter";
import { SingleSelectCell } from "./SingleSelectCell";
import { ProgressBarCell } from "./ProgressBarCell";
import { ViewDocumentCell } from "./ViewDocumentCell";
import { ScrollSync } from "react-scroll-sync";

export type Checkbox2<
  T extends { id: string | number } = { id: string | number }
> = {
  selectedIds: T[];
  setSelectedIds: React.Dispatch<React.SetStateAction<T[]>>;
  idColumn: string;
};
export type Checkbox = {
  selectedIds: (string | number)[];
  setSelectedIds: React.Dispatch<React.SetStateAction<(string | number)[]>>;
  idColumn: string;

  clickedIdIfMultiIds: string | number | null;
  setClickedIdIfMultiIds: React.Dispatch<
    React.SetStateAction<string | number | null>
  >;
};

export type PrimitiveFormatters = Partial<{
  boolean(b: boolean): React.ReactNode;
  string(s: string): React.ReactNode;
  number(n: number): React.ReactNode;
  date(d: Date): React.ReactNode;
  object(o: Object, columnKey?: string): React.ReactNode;
  array(a: Array<any>, columnKey?: string): React.ReactNode;
}>;

export type TableProps<T = any> = {
  idColumn: string;
  table?: TableInfo;
  primitiveFormatters?: PrimitiveFormatters;
  rowFormatter?(r: T): { [K in keyof T]: React.ReactNode };
  checkbox?: Checkbox;
  rowOptionComponent?(data: any): React.ReactNode;
  onFilterClick?(): void;
  hiddenColumns?: string[];
  widthSetting?: "Calculate" | "100%";
  rowHeight?: "default" | "large";
  rowButton?(data: any): React.ReactNode;
  onRowClick?(id: number): void;
  noRowHover?: boolean;
  splitFromLeft?: number;
  splitFromRight?: number;
  buttonInMiddleTable?: boolean;
  rowCellOptions?: { [K in keyof Partial<T>]: CellOptions };
  rowConfig?: RowConfig;
  showIdColumn?: boolean;
  disableMultiSelect?: boolean;
  customRowCells?: {
    [k in keyof T]?: TCustomCell<T>;
  };
  setCustomCellValue?: React.Dispatch<any>;
  customCellValue?: any;
  hideRowOptionsMenuComponent?: boolean;
  hideFilterTableButton?: boolean;
  showRightTable?: boolean;
  parentSelectedId?: number;
  leftTableReducedPadding?: boolean;
  refs?: { selectRef?: any; containerRef?: any };
};

function thousands_separators(num: number): string {
  return num.toLocaleString("en-US");
}

// Defaults
const defaultPrimitiveFormatters: PrimitiveFormatters = {
  boolean: (b: boolean) => (b ? <span>Yes</span> : <span>No</span>),
  date: (d: Date) => {
    return new Date(d).toDateString();
  },
  string: (s: string) => <span>{s}</span>,
  number: (n: number) => <span>{thousands_separators(n)}</span>,
};

export type FlexProps = {
  menuActions?: MenuState[1];
  menuActionsCode?: CrudOptions;
  setMenuActionsCode?: React.Dispatch<
    React.SetStateAction<CrudOptions | undefined>
  >;
  twoSingleClicks?: any;
};
const FlexTable: React.FC<TableProps & FlexProps> = ({
  idColumn,
  checkbox,
  table,
  onRowClick,
  onFilterClick,
  primitiveFormatters,
  widthSetting = "Calculate",
  rowHeight = "default",
  rowButton,
  rowOptionComponent,
  hiddenColumns = [],
  rowFormatter,
  noRowHover,
  splitFromLeft = 0,
  splitFromRight = 0,
  buttonInMiddleTable = false,
  rowCellOptions,
  rowConfig,
  showIdColumn,
  disableMultiSelect,
  customRowCells,
  setCustomCellValue,
  customCellValue,
  hideRowOptionsMenuComponent,
  hideFilterTableButton,
  showRightTable,
  menuActionsCode,
  setMenuActionsCode,
  parentSelectedId,
  leftTableReducedPadding,
  refs,
}) => {
  const [rowHovered, setRowHovered] = React.useState<number | null>(null);
  const [overflow, setOverflow] = React.useState<boolean>(false);

  const rawColumns: ColumnDetail[] =
    table && table.data ? table.data.details.columns : [];
  const rawListData: { [k: string]: any }[] =
    table && table.data ? (table.data.details.listData as any) : [];

  const processedColumns = rawColumns.filter(c => {
    if (
      !showIdColumn &&
      (c.columnKey as string).toLowerCase() === idColumn.toLowerCase()
    ) {
      return false;
    } else if (!c.bVisible) {
      return false;
    }

    return !hiddenColumns
      .map(x => x.toLowerCase())
      .includes(c.columnKey.toString().toLowerCase());
  });

  function formatPrimitives(value: unknown, key?: string) {
    const formatter = { ...defaultPrimitiveFormatters, ...primitiveFormatters };

    if (typeof value === "string" && formatter.string)
      return formatter.string(value);
    if (typeof value === "number" && formatter.number)
      return formatter.number(value);
    if (typeof value === "boolean" && formatter.boolean)
      return formatter.boolean(value);
    if (Array.isArray(value) && formatter.array) {
      return formatter.array(value, key);
    }
    if (value && typeof value === "object" && formatter.object)
      return formatter.object(value, key);

    return value;
  }

  const handleRowHover = (
    _e: React.MouseEvent<HTMLDivElement>,
    rowIndex: number
  ) => {
    setRowHovered(rowIndex);
  };

  const getTableWrapperClassName = () => {
    let className = "FlexTableWrapper";
    if (rowHeight === "large") {
      className += " LargeRow";
    }
    return className;
  };

  const [clickCount, setClickCount] = useState<number>(0);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const clickCountRef = useRef<number>(0);

  return (
    <>
      <div
        className={getTableWrapperClassName()}
        onMouseLeave={() => setRowHovered(null)}
      >
        <div className="Inner">
          <ScrollSync>
            <div
              className={`TableContainer ${
                !rawListData ||
                (Array.isArray(rawListData) && rawListData.length === 0)
                  ? "NoData"
                  : ""
              }`}
            >
              <LeftTable
                idColumn={idColumn}
                columns={processedColumns ?? []}
                toggleColumnSort={
                  table?.data?.actions &&
                  (table.data.actions.toggleColumnSort as (s: string) => void)
                }
                sortBy={table?.data?.details.sortBy as any}
                checkbox={checkbox}
                columnData={rawListData}
                rowHovered={rowHovered}
                handleMouseEnterRow={handleRowHover}
                onRowClick={onRowClick}
                formatPrimitives={formatPrimitives}
                rowFormatter={rowFormatter}
                noRowHover={noRowHover}
                columnCount={splitFromLeft}
                rowCellOptions={rowCellOptions}
                overflow={overflow}
                disableMultiSelect={disableMultiSelect}
                menuActionsCode={menuActionsCode}
                setMenuActionsCode={setMenuActionsCode}
                parentSelectedId={parentSelectedId}
                twoSingleClicks={{
                  clickCount,
                  setClickCount,
                  lastClickTime,
                  setLastClickTime,
                  clickCountRef: clickCountRef?.current,
                }}
                reducedPadding={leftTableReducedPadding}
              />

              <MiddleTable
                columns={
                  processedColumns?.filter((_, i) => {
                    let lowerBound = checkbox
                      ? splitFromLeft <= 1
                        ? 1
                        : splitFromLeft
                      : splitFromLeft;
                    const upperBound = rawColumns.length - splitFromRight;
                    return isBetween(i + 1, lowerBound, upperBound + 1);
                  }) ?? []
                }
                columnData={rawListData}
                toggleColumnSort={
                  table?.data?.actions &&
                  (table.data.actions.toggleColumnSort as (s: string) => void)
                }
                sortBy={table?.data?.details.sortBy as any}
                checkbox={checkbox}
                rowHovered={rowHovered}
                rowButton={buttonInMiddleTable ? rowButton : undefined}
                handleMouseEnterRow={handleRowHover}
                onRowClick={onRowClick}
                idColumn={idColumn}
                hiddenColumns={hiddenColumns}
                formatPrimitives={formatPrimitives}
                rowFormatter={rowFormatter}
                noRowHover={noRowHover}
                rowCellOptions={rowCellOptions}
                setOverflow={setOverflow}
                disableMultiSelect={disableMultiSelect}
                customRowCells={customRowCells}
                setCustomCellValue={setCustomCellValue}
                customCellValue={customCellValue}
                menuActionsCode={menuActionsCode}
                setMenuActionsCode={setMenuActionsCode}
                twoSingleClicks={{
                  clickCount,
                  setClickCount,
                  lastClickTime,
                  setLastClickTime,
                  clickCountRef: clickCountRef?.current,
                }}
                refs={refs}
                parentSelectedId={parentSelectedId}
                showRightTable={showRightTable}
              />

              {showRightTable && (
                <RightTable
                  idColumn={idColumn}
                  columns={processedColumns}
                  table={table as any}
                  checkbox={checkbox}
                  rawListData={rawListData}
                  rowHovered={rowHovered}
                  handleMouseEnterRow={handleRowHover}
                  onRowClick={onRowClick}
                  onFilterClick={onFilterClick}
                  formatPrimitives={formatPrimitives}
                  rowFormatter={rowFormatter}
                  noRowHover={noRowHover}
                  rowButton={rowButton}
                  rowOptionComponent={rowOptionComponent}
                  columnCount={splitFromRight}
                  buttonInMiddleTable={buttonInMiddleTable}
                  rowCellOptions={rowCellOptions}
                  rowConfig={rowConfig}
                  overflow={overflow}
                  customCellValue={customCellValue}
                  hideRowOptionsMenuComponent={hideRowOptionsMenuComponent}
                  hideFilterTableButton={hideFilterTableButton}
                />
              )}
            </div>
          </ScrollSync>
        </div>
      </div>
    </>
  );
};
export { FlexTable };

function useDoubleClickDetection(callback: (val?: any) => void, delay: number) {
  const clickCount = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (clickCount.current === 1) {
        callback("Single click");
      } else if (clickCount.current === 2) {
        callback("Double click");
      }
      clickCount.current = 0;
    }, delay);

    return () => clearTimeout(timer);
  }, [callback, delay]);

  const onDoubleClick = () => {
    clickCount.current += 1;
  };

  return { onDoubleClick };
}

type RowData = {
  node: React.ReactNode;
  options?: CellOptions;
  cellType?: CellType;

  label?: string;
};

export type RowConfig = {
  colourFilterHighlights?: { id: string; classNames: string }[];
};

export type MakeRow<T> = {
  id: string;
  r: T;
  index: number;
  columns: ColumnDetail<T>[];
  formatPrimitives: (v: unknown, key?: string) => unknown;
  hiddenColumns: string[];
  rowHovered: number | null;
  handleMouseEnterRow: (e: any, i: number) => void;
  onRowClick: (() => void) | null;
  rowButton?: () => React.ReactNode;
  formatRow?: (
    r: T,
    columnTypes?: ColumnTypes
  ) => { [K in keyof T]: React.ReactNode };
  noRowHover?: boolean;
  checkbox?: Checkbox;
  rowOptionComponent?: () => React.ReactNode;
  rowCellOptions?: { [K in keyof Partial<T>]: CellOptions };
  includeCheckbox?: boolean;
  includeFilterCell?: boolean;
  rowSelected?: boolean;
  hideRowOptionComponent?: boolean;
  disableMultiSelect?: boolean;
  customRowCells?: {
    [k in keyof T]?: TCustomCell<T>;
  };
  setCustomCellValue?: React.Dispatch<any>;
  customCellValue?: any;
  menuActionsCode?: CrudOptions;
  setMenuActionsCode?: React.Dispatch<
    React.SetStateAction<CrudOptions | undefined>
  >;

  onDoubleClick?: () => void;
  columnAutoWidths?: ColumnAutoWidths;
  parentSelectedId?: number;
  twoSingleClicks?: any;
  position: "Left" | "Middle" | "Right";
  refs?: { selectRef?: any; containerRef?: any };
};

export type ColumnTypes = { [columnKey: string]: string };

export function makeRow<T extends { [K: string]: React.ReactNode }>(
  props: MakeRow<T>
): React.ReactNode {
  const {
    id,
    r,
    index,
    columns,
    formatPrimitives,
    hiddenColumns,
    rowHovered,
    handleMouseEnterRow,
    onRowClick,
    rowButton,
    formatRow,
    noRowHover,
    checkbox,
    rowOptionComponent,
    rowCellOptions,
    includeFilterCell,
    rowSelected,
    hideRowOptionComponent,
    includeCheckbox,
    disableMultiSelect,
    customRowCells,
    setCustomCellValue,
    customCellValue,
    menuActionsCode,
    setMenuActionsCode,

    columnAutoWidths,
    parentSelectedId,
    twoSingleClicks,
    position,
    refs,
  } = props;

  const columnTypes = Object.fromEntries(
    columns.map(item => [
      typeof item.columnKey === "string"
        ? lowerCaseFirstLetter(item.columnKey)
        : item.columnKey,
      item.type,
    ])
  );

  const row = formatRow ? formatRow(r, columnTypes) : r;
  const columnKeys = columns.map(c => c.columnKey.toString().toLowerCase());
  const findOrderNumber = (key: keyof T) => {
    const col = columns.find(
      x => x.columnKey.toString().toLowerCase() === key.toString().toLowerCase()
    );
    if (!col) return 0;
    return col.orderNumber;
  };

  const pairs = toPairs(row)
    .filter(([k]) => {
      if (
        columnKeys.includes(k.toLowerCase()) &&
        !hiddenColumns.includes(k.toLowerCase())
      ) {
        return true;
      }

      return false;
    })
    .map(([k, v]) => {
      if (
        customRowCells &&
        Object.keys(customRowCells).includes(k) &&
        customRowCells[k]
      ) {
        if (customRowCells[k]?.cutomCell) {
          return [k, customRowCells[k]?.cutomCell] as [
            keyof T,
            React.ReactNode
          ];
        } else if (customRowCells[k]?.type === "SingleSelect") {
          return [
            k,
            <SingleSelectCell
              customRowCells={customRowCells}
              row={row}
              k={k}
              id={id}
              customCellValue={customCellValue}
              setCustomCellValue={setCustomCellValue}
              refs={refs}
              parentSelectedId={parentSelectedId}
            />,
          ] as [keyof T, React.ReactNode];
        } else if (customRowCells[k]?.type === "ProgressBar") {
          return [
            k,
            <ProgressBarCell
              customRowCells={customRowCells}
              row={row}
              k={k}
              id={id}
              customCellValue={customCellValue}
              setCustomCellValue={setCustomCellValue}
              refs={refs}
              parentSelectedId={parentSelectedId}
            />,
          ] as [keyof T, React.ReactNode];
        } else if (customRowCells[k]?.type === "ViewDocument") {
          return [
            k,
            <ViewDocumentCell
              customRowCells={customRowCells}
              row={row}
              k={k}
              id={id}
              customCellValue={customCellValue}
              setCustomCellValue={setCustomCellValue}
              refs={refs}
              parentSelectedId={parentSelectedId}
            />,
          ] as [keyof T, React.ReactNode];
        }
      }
      return [k, formatPrimitives(v, k)] as [keyof T, React.ReactNode];
    })
    .sort((a, b) => {
      const aOrder = findOrderNumber(a[0]);
      const bOrder = findOrderNumber(b[0]);
      if (aOrder === bOrder) return 0;
      return aOrder < bOrder ? -1 : 1;
    }) as [keyof T, React.ReactNode][];

  const handleDoubleClick = (e?: any) => {
    checkbox?.setSelectedIds(ids => [
      ...(disableMultiSelect ? [] : ids?.filter(i => i !== id) ?? []),
      id,
    ]); //working solution - double click wont affect all others

    checkbox?.setClickedIdIfMultiIds(Id => id);
    if (menuActionsCode) {
      setMenuActionsCode && setMenuActionsCode(menuActionsCode);
    }
  };
  const handleSingleClick = (currentTime?: number) => {
    if (checkbox) {
      const { setSelectedIds } = checkbox;
      if (rowSelected) {
        setSelectedIds(ids =>
          ids.filter(i => {
            return i.toString() !== id.toString();
          })
        );

        if (checkbox?.clickedIdIfMultiIds === id) {
          checkbox?.setClickedIdIfMultiIds(null);
        }
      } else {
        if (disableMultiSelect) {
          checkbox?.setClickedIdIfMultiIds(null);
          setSelectedIds([id]);
        } else {
          checkbox?.setClickedIdIfMultiIds(id);
          setSelectedIds(ids => [...ids, id]);
        }
      }
    }
  };
  const handleSelectionChange = (e?: any) => {
    if (twoSingleClicks) twoSingleClicks.clickCountRef += 1;

    setTimeout(() => {
      if (twoSingleClicks?.clickCountRef === 1) {
        handleSingleClick();
      } else if (twoSingleClicks?.clickCountRef === 2) {
        handleDoubleClick(e);
      }

      if (twoSingleClicks) twoSingleClicks.clickCountRef = 0;
    }, 300); // Adjust the delay as needed
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    handleSelectionChange(e);
  };

  const rowData = (): RowData[] => {
    let data: RowData[] = rowCellOptions
      ? pairs.map(p => ({
          node: p[1],
          options: {
            ...(rowCellOptions[p[0]] ?? {}),
          },
          cellType: customRowCells ? customRowCells[p[0]]?.type : undefined,
          label: p[0] as string,
        }))
      : pairs.map(p => ({
          node: p[1],
          cellType: customRowCells ? customRowCells[p[0]]?.type : undefined,
          label: p[0] as string,
        }));

    if (checkbox && includeCheckbox) {
      data = [
        {
          node: (
            <div key={`make-row-row-data-${index}`}>
              <input
                id={parentSelectedId ? `${parentSelectedId}-${id}` : id}
                type="checkbox"
                checked={rowSelected}
                onChange={handleCheckboxChange}
                key={`make-row-row-data-input-${index}`}
              />
              <label
                className="CustomInput"
                htmlFor={parentSelectedId ? `${parentSelectedId}-${id}` : id}
                key={`make-row-row-data-label-${index}`}
              />
            </div>
          ),
          cellType: "checkboxCell",
        },
        ...data,
      ];
    }

    if (rowButton) {
      data = [...data, { node: rowButton(), cellType: "buttonCell" }];
    }

    if (rowOptionComponent) {
      data = [
        ...data,
        { node: rowOptionComponent(), cellType: "rowOptionCell" },
      ];
    }

    return data;
  };

  return (
    <Fragment key={`make-row-fragment-${index}`}>
      {noRowHover ? (
        <div
          className={rowSelected ? "Row NoHover Selected" : "Row NoHover"}
          key={"RST_Row" + index}
          id={"Row" + id + "-" + position}
          style={{ position: "relative" }}
        >
          {rowData().map((r, i) => {
            let cellTooltipText: string | undefined = undefined;

            const columnName = r.label;

            const autoWidth =
              columnAutoWidths &&
              columnName &&
              columnAutoWidths.hasOwnProperty(columnName)
                ? columnAutoWidths[columnName]
                : undefined;

            if (
              columnName &&
              props.r &&
              typeof props.r === "object" &&
              props.r.hasOwnProperty(columnName)
            ) {
              const tempText = props.r[columnName as keyof typeof props.r];

              if (typeof tempText === "string") {
                cellTooltipText = tempText;
              } else if (tempText !== null && tempText !== undefined) {
                cellTooltipText = capitaliseStringFirstLetter(`${tempText}`);
              }
            }

            return (
              <Cell
                key={`make-row-cell-${i}`}
                onClick={
                  r.cellType !== "checkboxCell" &&
                  r.cellType !== "rowOptionCell" //&&
                    ? onRowClick ?? handleSelectionChange
                    : () => {}
                }
                options={{
                  cellType: r.cellType,

                  ...r.options,
                }}
                tooltipLabel={
                  r.cellType !== "ViewDocument" ? cellTooltipText : undefined
                }
                overrideWidth={autoWidth}
                noTooltip={r.cellType === "ViewDocument"}
              >
                {r.cellType === "rowOptionCell" && hideRowOptionComponent
                  ? null
                  : r.node}
              </Cell>
            );
          })}
          {includeFilterCell && (
            <Cell
              options={{ cellType: "filterCell", noSortIcon: true }}
              key={`make-row-filter-cell-${index}`}
            />
          )}
        </div>
      ) : (
        <div
          className={
            rowSelected
              ? "Row Selected"
              : rowHovered === index
              ? "Row Highlight"
              : "Row"
          }
          key={"RST_Row" + index}
          onMouseEnter={e => handleMouseEnterRow(e, index)}
          id={"Row" + id + "-" + position}
          style={{ position: "relative" }}
        >
          {rowData().map((r, i) => {
            let cellTooltipText: string | undefined = undefined;

            const columnName = r.label;

            if (
              columnName &&
              props.r &&
              typeof props.r === "object" &&
              props.r.hasOwnProperty(columnName)
            ) {
              const tempText = props.r[columnName as keyof typeof props.r];

              if (typeof tempText === "string") {
                cellTooltipText = tempText;
              } else if (tempText !== null && tempText !== undefined) {
                cellTooltipText = capitaliseStringFirstLetter(`${tempText}`);
              }
            }

            let cellDataType: string | undefined = undefined;

            if (
              columnName &&
              props.columns &&
              Array.isArray(props.columns) &&
              props.columns.length > 0 &&
              props.columns[0] &&
              typeof props.columns[0] === "object"
            ) {
              const parsedColumnName = capitaliseStringFirstLetter(columnName);

              const matchingColumnData = props.columns.filter(item => {
                return item.columnKey === parsedColumnName;
              });

              if (
                Array.isArray(matchingColumnData) &&
                matchingColumnData.length === 1 &&
                matchingColumnData[0] &&
                typeof matchingColumnData[0] === "object" &&
                matchingColumnData[0].hasOwnProperty("type")
              ) {
                const tempType = matchingColumnData[0].type;

                if (typeof tempType === "string") {
                  cellDataType = tempType;
                }
              }
            }

            const autoWidth =
              columnAutoWidths &&
              columnName &&
              columnAutoWidths.hasOwnProperty(columnName)
                ? columnAutoWidths[columnName]
                : undefined;

            return (
              <Cell
                key={`make-row-cell-2-${i}`}
                onClick={
                  r.cellType !== "checkboxCell" &&
                  r.cellType !== "rowOptionCell" //&&
                    ? onRowClick ?? handleSelectionChange
                    : () => {}
                }
                options={{
                  cellType: r.cellType,
                  ...r.options,
                  label: r.label as string,
                }}
                tooltipLabel={
                  r.cellType !== "ViewDocument" ? cellTooltipText : undefined
                }
                cellDataType={cellDataType}
                overrideWidth={autoWidth}
                noTooltip={r.cellType === "ViewDocument"}
              >
                {r.cellType === "rowOptionCell" && hideRowOptionComponent
                  ? null
                  : r.node}
              </Cell>
            );
          })}
          {includeFilterCell && (
            <Cell
              options={{ cellType: "filterCell", noSortIcon: true }}
              key={`make-row-filter-cell-2-${index}`}
            />
          )}
        </div>
      )}
    </Fragment>
  );
}

// helpers
const isBetween = (
  numOfInterest: number,
  lowerBound: number,
  higherBound: number
) => numOfInterest > lowerBound && numOfInterest < higherBound;
